/**
 * Listener de-duplication for repeated CDN platform initialization
 *
 * The CDN's platform init functions (initGTM, initGa4Gads, ...) add
 * document/window listeners every time they run — form-submit lead tracking,
 * login/sign-up tracking and the like. The SDK runs them once per page type to
 * register page-specific functions for SPA navigation, then once more for the
 * real page, so each listener would be installed several times and one form
 * submission would produce several generate_lead events.
 *
 * Each `runWithDedupedListeners` call is one *run* (one init pass). A
 * registration made in a run replaces the matching registration (same target,
 * event type, capture flag and handler source) of any *earlier* run: the
 * latest closure — the one built for the real page — stays, the copies from
 * the registration passes are removed. Registrations within the same run are
 * never de-duplicated against each other, so an init function that registers
 * one closure per configured property keeps all of them.
 */

type Registration = {
  run: number;
  target: EventTarget;
  type: string;
  listener: EventListener;
  options?: boolean | AddEventListenerOptions;
};

const registrations = new Map<string, Registration[]>();
let runCounter = 0;

function captureFlag(options?: boolean | AddEventListenerOptions): boolean {
  return typeof options === 'boolean' ? options : !!options?.capture;
}

function signature(targetName: string, type: string, listener: unknown, options?: boolean | AddEventListenerOptions): string | null {
  if (typeof listener !== 'function') return null;
  return `${targetName}|${type}|${captureFlag(options)}|${Function.prototype.toString.call(listener)}`;
}

function patchTarget(target: EventTarget, targetName: string, run: number): () => void {
  const hadOwn = Object.prototype.hasOwnProperty.call(target, 'addEventListener');
  const previousOwn = (target as any).addEventListener;
  // The target's own method (window's may come from another realm than the
  // EventTarget global, e.g. in jsdom)
  const original: EventTarget['addEventListener'] = previousOwn;

  (target as any).addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ): void {
    const key = signature(targetName, type, listener, options);
    if (key) {
      const kept: Registration[] = [];
      for (const previous of registrations.get(key) || []) {
        if (previous.run === run) {
          kept.push(previous);
        } else {
          previous.target.removeEventListener(previous.type, previous.listener, previous.options);
        }
      }
      kept.push({ run, target, type, listener: listener as EventListener, options });
      registrations.set(key, kept);
    }
    return original.call(target, type, listener, options);
  };

  return () => {
    if (hadOwn) {
      (target as any).addEventListener = previousOwn;
    } else {
      delete (target as any).addEventListener;
    }
  };
}

/**
 * Run `fn` (synchronous CDN init calls) as one run, de-duplicating its
 * document/window listener registrations against those of earlier runs.
 */
export function runWithDedupedListeners<T>(fn: () => T): T {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fn();
  }
  const run = ++runCounter;
  const restoreDocument = patchTarget(document, 'document', run);
  const restoreWindow = patchTarget(window, 'window', run);
  try {
    return fn();
  } finally {
    restoreWindow();
    restoreDocument();
  }
}
